/**
 * specExplain.js — read-only "how a section works" helpers for the Spec Library explainer.
 *
 * Turns the master template machinery into something a human can read and then hand to Claude Code:
 *   - ruleToEnglish(rule)      → a plain-language sentence for a visibility_rule
 *   - describeVariable(key)    → { source, label, detail } for a {{variable}} (question / derived /
 *                                project record / other), with the copyable key
 *   - parseTemplateNodes(tpl)  → a node tree (text / var / compare-with-children) for rendering the
 *                                body with highlighted variables and plain-English conditional callouts
 *
 * Nothing here mutates anything — the library is read-only; edits go through Claude Code.
 */

import { parseCompareArgs } from './specEngine.js'

// Derived flags computed in specEngine.applyDerivations — described in plain language so a reader can
// see WHY a section/clause appears without re-deriving the logic. Keep in sync with applyDerivations().
export const DERIVED_FLAGS = {
  div_02_required:               { label: 'Division 02 required',          detail: 'Computed. True for additions/remodels; for new builds, true when there is a demolition area or an existing structure to demolish.' },
  '04_cmu_section':              { label: 'CMU section included',          detail: 'Computed from the masonry scope (CMU walls, exposed or clad).' },
  include_misc_metals:           { label: 'Miscellaneous metals included', detail: 'Computed: true when steel stairs, guards, handrails, or a chimney shroud are present.' },
  include_division_05:           { label: 'Division 05 (Metals) included', detail: 'Computed: true with structural steel, or any miscellaneous metals.' },
  include_division_06:           { label: 'Division 06 (Wood) included',   detail: 'Computed: true when any structural or finish wood scope is selected.' },
  include_wood_framing:          { label: 'Wood framing included',         detail: 'Computed from the structural wood scope (light framing, heavy timber, wood stairs).' },
  include_fire_sprinkler_system: { label: 'Fire sprinkler included',       detail: 'Computed: true when the fire-suppression scope includes a new or modified sprinkler system.' },
  include_furring_strips:        { label: 'Furring strips included',       detail: 'Computed: true when exterior siding includes plank siding.' },
  include_misc_plumbing_systems: { label: 'Misc. plumbing included',       detail: 'Computed: true when landscaping is in the exterior improvements, or the plumbing scope includes leak detection.' },
  special_construction_section:  { label: 'Special construction (solar)',  detail: 'Computed: true when any solar energy scope is selected.' },
  interior_finishes_specifier:   { label: 'Interior-finishes specifier',   detail: 'Computed role (Architect / Interior Designer / Owner): Architect when the design package is "Architecture + Interiors"; otherwise Interior Designer or Owner depending on whether a third-party interior designer is involved. The package comes from the contract (the executed proposal’s Selected Package, or an Interior Architecture phase); the hand answer is only a fallback. When something is missing it reads "TBD (...)" naming what is missing.' },
  issue_date:                    { label: 'Issue date',                    detail: "Derived from the milestone's posted date — not a question." },
}

// Values that come straight from the Airtable Project record (injected by buildContext).
const PROJECT_FIELDS = {
  project_name: 'Project name', project_number: 'Project number', project_state: 'Project state',
  project_address: 'Project address', project_city: 'Project city', project_county: 'Project county',
  project_type: 'Project type', building_type: 'Building type', organization_name: 'Client organization',
  client_organization_name: 'Client organization', total_building_demolition_area: 'Total demolition area',
  project: 'Project record', project_concepts: 'Project record',
}

/** Classify a {{variable}} reference → { key, source, label, detail }. fieldsByKey is { key: field }. */
export function describeVariable(rawKey, fieldsByKey) {
  const key = String(rawKey || '').trim()
  const seg = key.split('.')[0].split(/\s+/)[0]
  const f = fieldsByKey && fieldsByKey[seg]
  if (f) {
    const opts = Array.isArray(f.options) && f.options.length ? ' · options: ' + f.options.join(', ') : ''
    return { key: seg, source: 'Question', label: f.label || seg, detail: 'Questionnaire input (' + (f.type || 'text') + ')' + opts }
  }
  if (DERIVED_FLAGS[seg]) return { key: seg, source: 'Derived', label: DERIVED_FLAGS[seg].label, detail: DERIVED_FLAGS[seg].detail }
  if (PROJECT_FIELDS[seg] || /project/i.test(seg)) return { key, source: 'Project record', label: PROJECT_FIELDS[seg] || key, detail: 'Pulled from the Airtable Project record.' }
  return { key: seg, source: 'Other', label: seg, detail: 'Resolved by the spec engine.' }
}

const OP_PHRASE = {
  '==': 'is', '!=': 'is not', contains: 'includes', notContains: 'does not include',
  isNotEmpty: 'is provided', isNotNull: 'is provided', isEmpty: 'is empty', isNull: 'is empty',
}

/** A visibility_rule (or compare cond) → a plain-language sentence using question labels. */
export function ruleToEnglish(rule, fieldsByKey) {
  if (!rule) return 'Always included.'
  if (Array.isArray(rule.all)) return rule.all.map(r => ruleToEnglish(r, fieldsByKey)).join(' AND ')
  if (Array.isArray(rule.any)) return rule.any.map(r => ruleToEnglish(r, fieldsByKey)).join(' OR ')
  if (rule.not) return 'NOT (' + ruleToEnglish(rule.not, fieldsByKey) + ')'
  if (rule.field) {
    const label = describeVariable(rule.field, fieldsByKey).label
    const op = OP_PHRASE[rule.op] || rule.op
    const val = (rule.op === 'isNotEmpty' || rule.op === 'isNotNull' || rule.op === 'isEmpty' || rule.op === 'isNull')
      ? '' : ' "' + String(rule.value == null ? '' : rule.value) + '"'
    return '“' + label + '” ' + op + val
  }
  return 'Always included.'
}

/**
 * Stable signature for a visibility_rule — identical conditions produce identical strings, so the
 * explainer can GROUP and COLOR lines that appear together. '' for an always-shown (null) rule.
 */
export function ruleSignature(rule) {
  if (!rule) return ''
  if (Array.isArray(rule.all)) return 'all(' + rule.all.map(ruleSignature).join('&') + ')'
  if (Array.isArray(rule.any)) return 'any(' + rule.any.map(ruleSignature).join('|') + ')'
  if (rule.not) return 'not(' + ruleSignature(rule.not) + ')'
  if (rule.field) return rule.field + '·' + rule.op + '·' + (rule.value == null ? '' : rule.value)
  return ''
}

/**
 * Break a rule into chip-able parts for compact visual display:
 *   { join: 'AND'|'OR'|null, parts: [{ text }] }
 * One level deep (the common case: an all/any of comparisons). Nested groups collapse to one part
 * each via ruleToEnglish, so a chip still reads correctly even for deeper logic.
 */
export function ruleToParts(rule, fieldsByKey) {
  if (!rule) return { join: null, parts: [] }
  if (Array.isArray(rule.all)) return { join: 'AND', parts: rule.all.map(r => ({ text: ruleToEnglish(r, fieldsByKey) })) }
  if (Array.isArray(rule.any)) return { join: 'OR', parts: rule.any.map(r => ({ text: ruleToEnglish(r, fieldsByKey) })) }
  return { join: null, parts: [{ text: ruleToEnglish(rule, fieldsByKey) }] }
}

/**
 * Parse a Documint template string into a render tree:
 *   { type:'text', text }
 *   { type:'var', key, md }
 *   { type:'compare', cond:{field,op,value}, children:[…], elseChildren:[…]|null }
 * Setup helpers ({{$var …}}, {{$group …}}, {{$filter …}}) are dropped (they don't render).
 */
export function parseTemplateNodes(tpl) {
  const root = []
  const stack = [{ node: null, target: root }]
  const cur = () => stack[stack.length - 1]
  const push = n => cur().target.push(n)
  const re = /\{\{\s*([^}]+?)\s*\}\}/g
  let last = 0, m
  const text = s => { if (s) push({ type: 'text', text: s }) }
  while ((m = re.exec(tpl || ''))) {
    text(tpl.slice(last, m.index)); last = re.lastIndex
    const inner = m[1].trim()
    if (inner.startsWith('#compare')) {
      const cond = parseCompareArgs(inner.replace(/^#compare\s*/, ''))
      const node = { type: 'compare', cond, children: [], elseChildren: null }
      push(node); stack.push({ node, target: node.children })
    } else if (inner === 'else') {
      const top = cur().node
      if (top && top.type === 'compare') { top.elseChildren = []; cur().target = top.elseChildren }
    } else if (inner.startsWith('/')) {
      if (stack.length > 1) stack.pop()
    } else if (inner.startsWith('$')) {
      const parts = inner.split(/\s+/)
      if (parts[0] === '$md' && parts[1]) push({ type: 'var', key: parts[1], md: true })
      // other $ helpers are setup-only — skip
    } else {
      push({ type: 'var', key: inner.split(/\s+/)[0] })
    }
  }
  text((tpl || '').slice(last))
  return root
}
